import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { deleteDocumentWithProgress } from '../utils/api';
import { useAuth } from './AuthContext';
import { useApi } from '../hooks/useApi';

const LibraryContext = createContext(null);

export function useLibrary() {
  const context = useContext(LibraryContext);
  if (!context) {
    throw new Error('useLibrary must be used within LibraryProvider');
  }
  return context;
}

const OPTIMISTIC_DOCS_KEY = 'optimisticDocuments';

// Helper to load optimistic documents from sessionStorage
function loadOptimisticDocuments() {
  try {
    const stored = sessionStorage.getItem(OPTIMISTIC_DOCS_KEY);
    if (!stored) return [];
    const docs = JSON.parse(stored);

    // Only keep documents that are actually in pending states
    const pendingStatuses = new Set(['uploading', 'processing', 'splitting', 'split_pending', 'deleting']);
    const now = Date.now();
    const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes

    return docs.filter(doc => {
      if (!doc || !pendingStatuses.has(doc.status)) return false;

      // Remove stale uploads (older than 10 minutes)
      if (doc.timestamp) {
        const age = now - doc.timestamp;
        if (age > MAX_AGE_MS) {
          console.log(`Removing stale ${doc.status} document: ${doc.name} (age: ${Math.floor(age / 1000)}s)`);
          return false;
        }
      }

      return true;
    });
  } catch (error) {
    console.error('Failed to load optimistic documents:', error);
    return [];
  }
}

// Helper to save optimistic documents to sessionStorage
function saveOptimisticDocuments(docs) {
  try {
    const pendingStatuses = new Set(['uploading', 'processing', 'splitting', 'split_pending', 'deleting']);
    const optimisticDocs = docs.filter(doc =>
      doc && (pendingStatuses.has(doc.status) || doc.isLocal === true)
    );
    sessionStorage.setItem(OPTIMISTIC_DOCS_KEY, JSON.stringify(optimisticDocs));
  } catch (error) {
    console.error('Failed to save optimistic documents:', error);
  }
}

export function LibraryProvider({ children }) {
  const { isAuthenticated, loading: authLoading } = useAuth(); // PC-139: Removed unused 'user'
  const { fetchDocuments: apiFetchDocuments, fetchAPI } = useApi();
  const [documents, setDocuments] = useState(() => loadOptimisticDocuments());
  const [loading, setLoading] = useState(false);
  const documentsRef = useRef([]);
  const deletingDocumentsRef = useRef(new Set()); // Track documents being deleted
  const hasCheckedStuckDeletions = useRef(false); // Track if we've checked for stuck deletions

  const fetchDocuments = useCallback(async () => {
    // Only show loading if we have no documents at all (first time user)
    // Otherwise, silently refresh in background
    const shouldShowLoading = documentsRef.current.length === 0;
    if (shouldShowLoading) {
      setLoading(true);
    }

    try {
      const data = await apiFetchDocuments();
      console.log(`[LibraryContext] 📥 Fetching documents. Current deletion tracking:`, Array.from(deletingDocumentsRef.current));

      const mappedDocuments = (data || [])
        .filter(doc => {
          // Filter out documents that are already deleted
          // These should not appear in the UI anymore
          if (doc.status === 'deleted') {
            console.log(`[LibraryContext] ⏭️  Skipping document ${doc.filename} - already deleted`);
            return false;
          }

          // Also filter out documents that are currently being deleted in the UI
          // This prevents deleted documents from reappearing while deletion is in progress
          const documentId = doc.document_id || (doc.SK ? doc.SK.replace(/^DOC#/, '') : null);
          if (documentId && deletingDocumentsRef.current.has(documentId)) {
            console.log(`[LibraryContext] ✅ Filtering out document ${documentId} (${doc.filename}) - still in deletion tracking. Server status: ${doc.status}`);
            return false;
          }

          return true;
        })
        .map(doc => {
          // Extract document ID from SK (e.g., "DOC#794347da-9e2a-4934-a0cf-be3a35bf5f21")
          const documentId = doc.document_id || (doc.SK ? doc.SK.replace(/^DOC#/, '') : null);

          // Extract org ID from PK (e.g., "ORG#mpd")
          const orgId = doc.org_id || doc.orgId || (doc.PK ? doc.PK.replace(/^ORG#/, '') : null);

          const kbStatus = doc.kb_status
            || doc.kbStatus
            || (doc.status === 'processed' ? 'ready' : null)
            || (doc.status === 'kb_failed' ? 'failed' : null);

          // Get created_by from document - prioritize the display-ready field from backend (PC-145)
          // Backend prepares created_by specifically for frontend display with full names
          // Fallback to email extraction for backward compatibility with old documents
          const createdByDisplay = doc.created_by || doc.createdBy;

          // If backend didn't provide created_by, fall back to email-based logic
          const createdByEmail = doc.uploaded_by_email
            || doc.uploaded_by  // Fallback to UUID for legacy docs
            || doc.uploader_email
            || doc.uploaderEmail
            || doc.owner_email
            || doc.ownerEmail
            || doc.user_email
            || doc.userEmail
            || 'Unknown';

          // Priority: use created_by if available (full name), else extract from email
          const createdBy = createdByDisplay || (
            createdByEmail?.includes('@')
              ? createdByEmail.split('@')[0]  // Email: extract username part
              : createdByEmail                 // Full name or "Unknown" - use as-is
          );

          // For split documents, prefer original document URL over part URLs
          const documentUrl = doc.original_presigned_url
            || doc.original_url
            || doc.source_url
            || doc.source_presigned_url
            || doc.presigned_url
            || doc.url;

          // Debug logging for split documents to help identify correct field
          if (doc.is_split || doc.isSplit || doc.status === 'splitting' || doc.status === 'split_pending') {
            console.log(`[LibraryContext] Split document detected: ${doc.filename}`, {
              has_original_presigned_url: !!doc.original_presigned_url,
              has_original_url: !!doc.original_url,
              has_source_url: !!doc.source_url,
              has_source_presigned_url: !!doc.source_presigned_url,
              has_presigned_url: !!doc.presigned_url,
              has_url: !!doc.url,
              using_url: documentUrl ? 'found' : 'missing'
            });
          }

          // Clean up split part suffixes from filename for display
          // Split documents should show original filename, not "_part01.pdf" etc.
          const displayName = doc.filename?.replace(/_part\d{2}\.pdf$/i, '.pdf') || doc.filename;

          return {
            id: documentId,
            name: displayName,
            url: documentUrl,
            orgId,
            policyId: doc.policy_id || doc.policyId,
            createdBy,
            modifiedAt: doc.uploaded_at,
            completedAt: doc.completed_at || doc.last_updated,
            status: doc.status,
            kbStatus,
            kbFailureReason: doc.kb_failure_reason || doc.kbFailureReason,
            skipReason: doc.reason,
            fileSize: doc.file_size,
            chunkCount: doc.chunk_count,
            policyNumber: doc.policy_number,
            policyTitle: doc.policy_title,
            deletionProgress: doc.deletion_progress ?? doc.deletionProgress,
            deletionTimeRemaining: doc.deletion_time_remaining ?? doc.deletionTimeRemaining,
            deletionJobStatus: doc.deletion_job_status ?? doc.deletionJobStatus,
            kbJobId: doc.kb_job_id || doc.kbJobId,
            // PC-193: normalise to fileHash; support both SHA-256 (file_hash)
            // and legacy MD5 (md5_hash) from old DynamoDB records
            fileHash: doc.file_hash || doc.md5_hash || doc.fileHash || doc.md5Hash
          };
        });
      const serverDocumentIds = new Set(mappedDocuments.map((doc) => doc.id));
      const pendingStatuses = new Set(['uploading', 'processing', 'splitting', 'split_pending', 'deleting']);
      const optimisticDocuments = documentsRef.current.filter((doc) => {
        if (!doc?.id) return false;
        if (serverDocumentIds.has(doc.id)) return false;
        if (pendingStatuses.has(doc.status)) return true;
        return doc.isLocal === true;
      });

      let mergedDocuments = [...mappedDocuments, ...optimisticDocuments];
      console.log(`[LibraryContext] 📋 Merged documents: ${mergedDocuments.length} total (${mappedDocuments.length} from server, ${optimisticDocuments.length} optimistic)`);

      // Detect and remove duplicates by file hash (SHA-256 for new docs, MD5 for legacy)
      const hashGroups = {};
      const filenameGroups = {};

      mergedDocuments.forEach(doc => {
        // Group by file hash (most reliable for duplicates); PC-193 renamed md5Hash → fileHash
        if (doc.fileHash) {
          if (!hashGroups[doc.fileHash]) {
            hashGroups[doc.fileHash] = [];
          }
          hashGroups[doc.fileHash].push(doc);
        }

        // Also group by filename for logging
        if (!filenameGroups[doc.name]) {
          filenameGroups[doc.name] = [];
        }
        filenameGroups[doc.name].push(doc);
      });

      // Log documents without file hashes for debugging
      const docsWithoutHash = mergedDocuments.filter(d => !d.fileHash);
      if (docsWithoutHash.length > 0) {
        console.warn(`[LibraryContext] ⚠️  ${docsWithoutHash.length} documents missing file hash:`, docsWithoutHash.map(d => ({
          id: d.id,
          name: d.name,
          status: d.status,
          isLocal: d.isLocal
        })));
      }

      // Find duplicates by file hash and keep only the best version
      const duplicatesByHash = Object.entries(hashGroups).filter(([_, docs]) => docs.length > 1);
      const duplicatesByFilename = Object.entries(filenameGroups).filter(([_, docs]) => docs.length > 1);

      if (duplicatesByHash.length > 0 || duplicatesByFilename.length > 0) {
        console.warn(`[LibraryContext] ⚠️  Found duplicates: ${duplicatesByHash.length} by file hash, ${duplicatesByFilename.length} by filename`);

        // Deduplicate by file hash
        const docsToRemove = new Set();
        duplicatesByHash.forEach(([hash, docs]) => {
          console.warn(`[LibraryContext] 🔍 File hash ${hash.substring(0, 8)}... has ${docs.length} copies:`);

          // Sort by status priority and timestamp
          const statusPriority = {
            'processed': 5,
            'processing': 4,
            'splitting': 3,
            'split_pending': 3,
            'uploading': 2,
            'failed': 1,
            'skipped': 0
          };

          const sortedDocs = [...docs].sort((a, b) => {
            // Prefer non-local over local
            if (a.isLocal && !b.isLocal) return 1;
            if (!a.isLocal && b.isLocal) return -1;

            // Status priority
            const aPriority = statusPriority[a.status] || 0;
            const bPriority = statusPriority[b.status] || 0;
            if (aPriority !== bPriority) {
              return bPriority - aPriority;
            }

            // Prefer newer
            const aTime = new Date(a.completedAt || a.modifiedAt || 0).getTime();
            const bTime = new Date(b.completedAt || b.modifiedAt || 0).getTime();
            return bTime - aTime;
          });

          const docToKeep = sortedDocs[0];
          const docsToDiscard = sortedDocs.slice(1);

          console.log(`  ✅ Keeping: ${docToKeep.name} (ID: ${docToKeep.id}, status: ${docToKeep.status})`);
          docsToDiscard.forEach(doc => {
            console.log(`  🗑️  Removing: ${doc.name} (ID: ${doc.id}, status: ${doc.status})`);
            docsToRemove.add(doc.id);
          });
        });

        // Log filename duplicates that don't have file hash matches (different files, same name)
        duplicatesByFilename.forEach(([filename, docs]) => {
          const uniqueHashes = new Set(docs.map(d => d.fileHash).filter(Boolean));
          console.warn(`[LibraryContext] 📝 Filename "${filename}" duplicates:`, docs.map(d => ({
            id: d.id,
            status: d.status,
            fileHash: d.fileHash ? d.fileHash.substring(0, 8) + '...' : 'MISSING',
            isLocal: d.isLocal
          })));

          if (uniqueHashes.size > 1) {
            console.warn(`  ⚠️  WARNING: Same filename but different content (${uniqueHashes.size} different file hashes)`);
          } else if (uniqueHashes.size === 0) {
            console.warn(`  ⚠️  WARNING: No file hashes available - cannot verify if files are truly duplicates`);
          }
        });

        // Remove duplicates
        if (docsToRemove.size > 0) {
          const originalCount = mergedDocuments.length;
          mergedDocuments = mergedDocuments.filter(doc => !docsToRemove.has(doc.id));
          console.log(`[LibraryContext] ✅ Removed ${docsToRemove.size} duplicate(s): ${originalCount} → ${mergedDocuments.length} documents`);
        }
      }

      setDocuments(mergedDocuments);

      // Save optimistic documents to sessionStorage
      saveOptimisticDocuments(mergedDocuments);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      // Only update loading state if we were showing it
      if (shouldShowLoading) {
        setLoading(false);
      }
    }
  }, [apiFetchDocuments]); // PC-139: Removed user dependencies - no longer needed after removing fallback to current user

  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchDocuments();
    }
  }, [authLoading, isAuthenticated, fetchDocuments]);

  // Detect and handle stuck deletions on page load
  useEffect(() => {
    // Only run once on mount after documents are loaded
    if (hasCheckedStuckDeletions.current || documents.length === 0) return;

    const stuckDeletions = documents.filter(doc => doc.status === 'deleting');

    if (stuckDeletions.length > 0) {
      hasCheckedStuckDeletions.current = true; // Mark as checked
      console.warn('[LibraryContext] 🚨 Detected stuck deletions from cache after page load:', stuckDeletions.map(d => ({ id: d.id, name: d.name, isLocal: d.isLocal })));
      console.log('[LibraryContext] These were likely completed but cache is stale. Clearing optimistic cache and refreshing...');

      // Clear optimistic cache to remove stale deletion states
      sessionStorage.removeItem(OPTIMISTIC_DOCS_KEY);

      // Remove stuck deletions from current state immediately
      setDocuments(prev => prev.filter(doc => doc.status !== 'deleting'));

      // Force refresh to get fresh data from backend
      setTimeout(() => {
        fetchDocuments();
        console.log('[LibraryContext] ✅ Refreshed documents after clearing stale deletion cache');
      }, 500);
    } else if (documents.length > 0) {
      // No stuck deletions found, mark as checked
      hasCheckedStuckDeletions.current = true;
    }
  }, [documents, fetchDocuments]); // Include all dependencies

  const addDocument = useCallback((document) => {
    setDocuments((prev) => {
      const newDocs = [
        { isLocal: true, timestamp: Date.now(), ...document },
        ...prev
      ];
      // Save to sessionStorage for persistence across refreshes
      saveOptimisticDocuments(newDocs);
      return newDocs;
    });
  }, []);

  const updateDocumentStatus = useCallback((documentId, statusUpdate) => {
    setDocuments((prev) => {
      const updatedDocs = prev.map((doc) =>
        doc.id === documentId
          ? { ...doc, ...statusUpdate }
          : doc
      );
      // Save to sessionStorage for persistence across refreshes
      saveOptimisticDocuments(updatedDocs);
      return updatedDocs;
    });
  }, []);

  const removeDocument = useCallback(async (documentOrId, onProgress = null) => {
    const documentMetadata = typeof documentOrId === 'string'
      ? documentsRef.current.find((doc) => doc.id === documentOrId)
      : documentOrId;

    const documentId = typeof documentOrId === 'string'
      ? documentOrId
      : documentMetadata?.id || documentMetadata?.documentId || documentMetadata?.document_id;

    if (!documentId) {
      throw new Error('Document ID is required to delete a document');
    }

    if (!documentMetadata) {
      const message = `Cannot delete document ${documentId}: metadata not found`;
      console.warn(message);
      throw new Error(message);
    }

    // Handle optimistic (local-only) documents that haven't finished uploading
    // These should be removed from UI immediately without backend call
    const localStatuses = ['uploading', 'processing', 'splitting', 'split_pending'];
    if (documentMetadata.isLocal && localStatuses.includes(documentMetadata.status)) {
      console.log(`Removing optimistic document from UI: ${documentMetadata.name}`);
      setDocuments((prev) => {
        const filtered = prev.filter((doc) => doc.id !== documentId);
        saveOptimisticDocuments(filtered);
        return filtered;
      });

      // Clear policy checker caches when local document is removed
      sessionStorage.removeItem('libraryDocumentsCache');

      const savedUploads = sessionStorage.getItem('policyCheckerSessionUploads');
      if (savedUploads) {
        try {
          const uploads = JSON.parse(savedUploads);
          const filteredUploads = uploads.filter(u => u.id !== documentId);
          sessionStorage.setItem('policyCheckerSessionUploads', JSON.stringify(filteredUploads));
        } catch (e) {
          console.warn('[LibraryContext] Failed to filter policy checker uploads (local):', e);
        }
      }

      const savedProgress = sessionStorage.getItem('fileUploadProgress');
      if (savedProgress) {
        try {
          const progress = JSON.parse(savedProgress);
          const filteredProgress = progress.filter(f => f.id !== documentId);
          sessionStorage.setItem('fileUploadProgress', JSON.stringify(filteredProgress));
        } catch (e) {
          console.warn('[LibraryContext] Failed to filter file upload progress (local):', e);
        }
      }

      console.log(`[LibraryContext] 🧹 Cleared policy checker progress for removed local document ${documentId}`);

      // Dispatch event to notify components that a document was deleted
      window.dispatchEvent(new CustomEvent('documentDeleted', { detail: { documentId } }));

      // Notify that the deletion operation completed
      window.dispatchEvent(new Event('refreshOperations'));

      return; // Exit early, no backend call needed
    }

    const orgId = documentMetadata?.orgId || documentMetadata?.org_id;
    const policyId = documentMetadata?.policyId || documentMetadata?.policy_id;
    const status = documentMetadata?.status;

    // Allow deletion without policyId for skipped/not processed/failed/processing documents (never fully processed or stuck)
    const isUnprocessed = status === 'skipped' || status === 'not processed' || status === 'failed' || status === 'processing';
    if (!orgId || (!policyId && !isUnprocessed)) {
      const message = `Missing orgId or policyId for document ${documentId} deletion`;
      console.warn(message, { documentId, orgId, policyId, status });
      throw new Error(message);
    }

    try {
      // Mark document as being deleted to prevent it from reappearing in refresh cycles
      deletingDocumentsRef.current.add(documentId);
      console.log(`[LibraryContext] 🗑️  Added document ${documentId} (${documentMetadata.name}) to deletion tracking. Current tracking set:`, Array.from(deletingDocumentsRef.current));
      console.log(`[LibraryContext] Document metadata for tracking:`, {
        id: documentId,
        name: documentMetadata.name,
        orgId,
        policyId,
        status
      });

      // Update document to show "deleting" status immediately
      updateDocumentStatus(documentId, {
        status: 'deleting',
        deletionProgress: 0
      });

      // Notify PolicyChecker and OperationsIndicator that a deletion operation started
      window.dispatchEvent(new Event('refreshOperations'));

      // Start async deletion with progress tracking
      await deleteDocumentWithProgress(fetchAPI, {
        documentId,
        orgId,
        policyId
      }, (progressData) => {
        console.log(`[LibraryContext] 📊 Deletion progress update for ${documentId}:`, progressData);

        // If deletion is complete, remove document immediately
        // This prevents race conditions with refresh cycles
        if (progressData.status === 'deleted' || progressData.progress_percent === 100) {
          console.log(`[LibraryContext] ✅ Deletion complete for ${documentId}. Removing from UI.`);
          setDocuments((prev) => {
            const filtered = prev.filter((doc) => doc.id !== documentId);
            console.log(`[LibraryContext] Removed document from state. Remaining documents:`, filtered.length);
            saveOptimisticDocuments(filtered);
            return filtered;
          });

          // Clear policy checker caches when document is deleted
          sessionStorage.removeItem('libraryDocumentsCache');

          // Clear from policyCheckerSessionUploads
          const savedUploads = sessionStorage.getItem('policyCheckerSessionUploads');
          if (savedUploads) {
            try {
              const uploads = JSON.parse(savedUploads);
              const filteredUploads = uploads.filter(u => u.id !== documentId);
              sessionStorage.setItem('policyCheckerSessionUploads', JSON.stringify(filteredUploads));
            } catch (e) {
              console.warn('[LibraryContext] Failed to filter policy checker uploads:', e);
            }
          }

          // Clear from fileUploadProgress (FileUpload component state)
          const savedProgress = sessionStorage.getItem('fileUploadProgress');
          if (savedProgress) {
            try {
              const progress = JSON.parse(savedProgress);
              const filteredProgress = progress.filter(f => f.id !== documentId);
              sessionStorage.setItem('fileUploadProgress', JSON.stringify(filteredProgress));
            } catch (e) {
              console.warn('[LibraryContext] Failed to filter file upload progress:', e);
            }
          }

          console.log(`[LibraryContext] 🧹 Cleared policy checker progress for deleted document ${documentId}`);

          // Dispatch event to notify components that a document was deleted
          window.dispatchEvent(new CustomEvent('documentDeleted', { detail: { documentId } }));

          // Notify that the deletion operation completed
          window.dispatchEvent(new Event('refreshOperations'));

          // IMPORTANT: Keep document in deletingDocumentsRef for a bit longer
          // to prevent it from reappearing during backend propagation delay
          // Clear it after 30 seconds to handle slow backend updates
          setTimeout(() => {
            deletingDocumentsRef.current.delete(documentId);
            console.log(`[LibraryContext] 🧹 Cleared deletion tracking for ${documentId} after delay`);
          }, 30000);

          // Call the external progress callback if provided
          if (onProgress) {
            onProgress(progressData);
          }
          return;
        }

        // Update document with progress information
        updateDocumentStatus(documentId, {
          status: progressData.status,
          deletionProgress: progressData.progress_percent,
          deletionTimeRemaining: progressData.estimated_time_remaining,
          deletionJobStatus: progressData.job_status,
          kbSyncRequestId: progressData.kb_sync_request_id || progressData.kbSyncRequestId || progressData.kb_job_id || progressData.kbJobId,
          kbJobId: progressData.kb_job_id || progressData.kbJobId
        });

        // Call the external progress callback if provided
        if (onProgress) {
          onProgress(progressData);
        }
      });

      // Remove document from list once deletion is complete (fallback)
      // This should already be done in the progress callback, but keep as safety net
      setDocuments((prev) => {
        const filtered = prev.filter((doc) => doc.id !== documentId);
        saveOptimisticDocuments(filtered);
        return filtered;
      });

      // Clear policy checker caches when document is deleted (fallback)
      sessionStorage.removeItem('libraryDocumentsCache');

      const savedUploads = sessionStorage.getItem('policyCheckerSessionUploads');
      if (savedUploads) {
        try {
          const uploads = JSON.parse(savedUploads);
          const filteredUploads = uploads.filter(u => u.id !== documentId);
          sessionStorage.setItem('policyCheckerSessionUploads', JSON.stringify(filteredUploads));
        } catch (e) {
          console.warn('[LibraryContext] Failed to filter policy checker uploads (fallback):', e);
        }
      }

      const savedProgress = sessionStorage.getItem('fileUploadProgress');
      if (savedProgress) {
        try {
          const progress = JSON.parse(savedProgress);
          const filteredProgress = progress.filter(f => f.id !== documentId);
          sessionStorage.setItem('fileUploadProgress', JSON.stringify(filteredProgress));
        } catch (e) {
          console.warn('[LibraryContext] Failed to filter file upload progress (fallback):', e);
        }
      }

      // Keep document in deletingDocumentsRef for 30s to prevent reappearing during propagation
      setTimeout(() => {
        deletingDocumentsRef.current.delete(documentId);
        console.log(`[LibraryContext] Cleared deletion tracking for ${documentId} after successful deletion`);
      }, 30000);
    } catch (error) {
      console.error('Failed to delete document:', error);

      // Check if this is a timeout error (504) - deletion might still succeed
      const isTimeout = error.status === 504 || error.message?.includes('timeout');

      if (isTimeout) {
        console.log(`[LibraryContext] Deletion timed out (504), but may still be processing. Checking status...`);

        // Wait a bit for backend to process, then check
        await new Promise(r => setTimeout(r, 5000));
      }

      // Always refresh to get current state from backend
      // This handles cases where deletion succeeded but status polling failed
      try {
        await fetchDocuments();

        // After refresh, check if document still exists in the fetched data
        // If it doesn't exist, deletion succeeded despite the error
        const stillExists = documentsRef.current.some(doc => doc.id === documentId);

        if (!stillExists) {
          // Document is gone - deletion succeeded, keep in deletingDocumentsRef for propagation
          console.log(`[LibraryContext] Document ${documentId} deleted successfully despite ${isTimeout ? 'timeout' : 'error'}`);
          setTimeout(() => {
            deletingDocumentsRef.current.delete(documentId);
          }, 30000);
          // Don't throw error since deletion actually succeeded
          return;
        } else {
          // Document still exists - deletion truly failed, clear tracking immediately
          console.log(`[LibraryContext] Document ${documentId} deletion failed, clearing tracking`);
          deletingDocumentsRef.current.delete(documentId);
        }
      } catch (refreshError) {
        console.error('Failed to refresh documents after deletion error:', refreshError);

        // Last resort: Remove document from UI if we can't verify
        // Better to show incomplete list than stuck "deleting" state
        setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));

        // Keep in deletingDocumentsRef temporarily in case deletion actually succeeded
        setTimeout(() => {
          deletingDocumentsRef.current.delete(documentId);
        }, 30000);
      }

      // Notify that the deletion operation completed/failed
      window.dispatchEvent(new Event('refreshOperations'));

      throw error;
    }
  }, [updateDocumentStatus, fetchDocuments, fetchAPI]);

  // Manual cleanup function for stuck deletions
  const clearStuckDeletions = useCallback(() => {
    console.log('[LibraryContext] 🧹 Manually clearing stuck deletions...');
    const stuckDeletions = documentsRef.current.filter(doc => doc.status === 'deleting');

    if (stuckDeletions.length > 0) {
      console.warn('[LibraryContext] Found stuck deletions:', stuckDeletions.map(d => ({ id: d.id, name: d.name })));

      // Clear sessionStorage
      sessionStorage.removeItem(OPTIMISTIC_DOCS_KEY);

      // Clear deletion tracking
      deletingDocumentsRef.current.clear();

      // Remove from state
      setDocuments(prev => prev.filter(doc => doc.status !== 'deleting'));

      // Refresh from server
      setTimeout(() => {
        fetchDocuments();
        console.log('[LibraryContext] ✅ Cleared stuck deletions and refreshed');
      }, 100);
    } else {
      console.log('[LibraryContext] No stuck deletions found');
    }
  }, [fetchDocuments]);

  const removeLocalDocument = useCallback((documentId) => {
    setDocuments((prev) => {
      const updated = prev.filter((doc) => doc.id !== documentId);
      saveOptimisticDocuments(updated);
      return updated;
    });
  }, []);

  const value = useMemo(() => ({
    documents,
    loading,
    addDocument,
    removeDocument,
    removeLocalDocument,
    updateDocumentStatus,
    refreshDocuments: fetchDocuments,
    clearStuckDeletions
  }), [documents, loading, addDocument, removeDocument, removeLocalDocument, updateDocumentStatus, fetchDocuments, clearStuckDeletions]);

  return <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>;
}
